import { Directive, EventEmitter, inject, Injector, Input, OnDestroy, OnInit, Output } from '@angular/core';
import {
    FiChecklist2ReportHelperService,
    ISaveChecklist2ReportFilterArgs,
} from '../services/fi-checklist2-report-helper.service';
import { BehaviorSubject, Subject } from 'rxjs';
import {
    IChecklist2ReportAttentionRequired,
    IChecklist2ReportChecklistSummary,
    IChecklistV2FilterAvailableItems,
    IChecklistV2ReportParameter,
    IChecklistV2ReportParameterFilter,
} from './fi-checklist2-report';
import { cloneDeep, isDefined, isEqual } from '../../../utils';
import { guid } from '../../../utils/guid';
import { FiChecklist2ReportService } from '../services/fi-checklist2-report.service';
import { FiSnackBarService } from '../../../fi-snackbar/snack-bar.service';
import { AppTextService } from '../../../app-text';
import { FiChecklist2ReportFilterService } from '../services/fi-checklist2-report-filter.service';
import { takeUntil } from 'rxjs/operators';
import { FiChecklist2ReportData } from './fi-checklist2-report-data';
import { FiChecklist2ReportDialogsService } from '@fi-shared/checklist/fi-checklist2-report/services/fi-checklist2-report-dialogs.service';
import { FiDialogsService } from '@fi-shared/fi-dialogs';
import { FiChecklist2ReportDataService } from '@fi-shared/checklist/fi-checklist2-report/services/fi-checklist2-report-data.service';
import { SaveChangesWarningService } from '@fi-shared/fi-shared-services/save-changes-warning.service';
import { FiChecklist2ReportRecentActivityService } from '@fi-shared/checklist/fi-checklist2-report/services/fi-checklist2-report-recent-activity.service';
import { FiChecklist2ReportType } from '@fi-shared/checklist/fi-checklist2-report/enums/fi-checklist2-report-type';
import { FiItemsListFilterHelperService } from '@fi-app/shared/fi-items-list/fi-items-list-filter/fi-items-list-filter-helper.service';
import { ItemsListFilterDialogSnackbarService } from '@fi-shared/fi-items-list/fi-items-list-filter/services/items-list-filter-dialog-snackbar.service';

@Directive()
export abstract class ReportWidgetDetailsFilterTableBaseDirective implements OnInit, OnDestroy {
    @Input() isFilterExpanded: boolean;
    @Input() reportParam: IChecklistV2ReportParameter;
    @Output() reportParamChange = new EventEmitter<IChecklistV2ReportParameter>();
    @Input() reportParametersCount: number;
    @Input() reportData: FiChecklist2ReportData;
    @Input() isSearchShown: boolean;
    @Input() isTableItemSelectionPrevented: boolean;
    @Input() isUpdateFilterAvailable: boolean = true;
    @Input() isSavedFiltersHidden: boolean = false;
    @Input() isResetFilterForceShown: boolean;
    @Output() currentFilterChange = new EventEmitter<IChecklistV2ReportParameterFilter>();

    isFilterExpanded$ = new BehaviorSubject<boolean>(false);
    currentFilter: IChecklistV2ReportParameterFilter;
    initialFilter: IChecklistV2ReportParameterFilter;
    clearSelectedItems$ = new Subject<void>();
    private _hasFilterUnsavedChanges: boolean = false;

    get availableItems(): IChecklistV2FilterAvailableItems {
        return this.reportData.availableItems;
    }

    protected fiChecklist2ReportHelperService = inject(FiChecklist2ReportHelperService);
    protected fiChecklist2ReportDialogsService = inject(FiChecklist2ReportDialogsService);
    protected fiChecklist2ReportFilterService = inject(FiChecklist2ReportFilterService);
    protected fiChecklist2ReportDataService = inject(FiChecklist2ReportDataService);
    protected checklist2ReportService = inject(FiChecklist2ReportService);
    protected fiSnackbarService = inject(FiSnackBarService);
    protected appTextService = inject(AppTextService);
    protected fiDialogsService = inject(FiDialogsService);
    protected saveChangesWarningService = inject(SaveChangesWarningService);
    protected fiItemsListFilterHelperService = inject(FiItemsListFilterHelperService);
    protected itemsListFilterDialogSnackbarService = inject(ItemsListFilterDialogSnackbarService);

    get hasFilterUnsavedChanges() {
        return this._hasFilterUnsavedChanges && this.isUpdateFilterAvailable;
    }

    constructor(private injector: Injector) {}

    ngOnInit() {
        this.setInitialFilterData();
        this.subscribeOnFilterVisibilityChange();
        this.subscribeOnFilterSave();
        // if filter not empty - show it expanded from start
        // https://chainformation.atlassian.net/browse/FI-6215?focusedCommentId=58691
        let isFilterExpanded =
            this.isFilterExpanded || !this.fiChecklist2ReportHelperService.isFilterEmpty(this.currentFilter);
        this.isFilterExpanded$.next(isFilterExpanded);
        if (this.fiChecklist2ReportHelperService.isCustomReportWidget(this.reportParam)) {
            this.setSelectedFilter(this.reportParam.Settings.CurrentFilterGuid);
        }
        this.setInitialSaveFilterActionsVisibility();
    }

    protected setInitialFilterData() {
        /*const existingFilter: IChecklistV2ReportParameterFilter = this.reportParam.Settings.Filters?.find((filter: IChecklistV2ReportParameterFilter) => {
            return filter.Guid === this.reportParam.Settings.CurrentFilterGuid;
        });
        this.currentFilter = isDefined(existingFilter) ? cloneDeep(existingFilter) : this.getNewFilterInstance(existingFilter);
        this.initialFilter = cloneDeep(this.currentFilter);*/

        if (
            !this.fiChecklist2ReportHelperService.isCustomReportWidget(this.reportParam) &&
            this.reportParam.ReportType !== FiChecklist2ReportType.RecentActivity
        ) {
            this.fiChecklist2ReportDataService.reset();
            this.reportParam.Settings.CurrentFilterGuid = '';
        }
        this.fiChecklist2ReportDataService.resetWidgetFilter(this.reportParam);
        this.currentFilter = this.fiChecklist2ReportDataService.getWidgetFilter(this.reportParam);
        this.initialFilter = cloneDeep(this.currentFilter);
    }

    private subscribeOnFilterSave() {
        this.fiChecklist2ReportHelperService
            .onSaveFilterAsNewWidget$()
            .pipe(takeUntil(this.ngOnDestroy$))
            .subscribe(() => this.saveFilterAsNewWidget());
        this.fiChecklist2ReportHelperService
            .onSaveFilter$()
            .pipe(takeUntil(this.ngOnDestroy$))
            .subscribe((args) => this.saveFilter(args));
    }

    private setInitialSaveFilterActionsVisibility() {
        if (this.isFilterChanged()) {
            this.fiChecklist2ReportHelperService.changeSaveFilterButtonVisibilityChange(true);
        }
    }

    private subscribeOnFilterVisibilityChange() {
        this.fiChecklist2ReportHelperService
            .onToggleWidgetDetailsFilterContainerVisibility$()
            .pipe(takeUntil(this.ngOnDestroy$))
            .subscribe(() => this.toggleFilterContainerVisibility());
    }

    setSelectedFilter(filterGuid: string) {
        const newFilter: IChecklistV2ReportParameterFilter = this.reportParam.Settings.Filters.find(
            (filter: IChecklistV2ReportParameterFilter) => filter.Guid === filterGuid,
        );
        this.currentFilter = cloneDeep(newFilter);
        this.initialFilter = cloneDeep(this.currentFilter);
        this.reportParam.Settings.CurrentFilterGuid = this.currentFilter.Guid;
    }

    private isFilterChanged(): boolean {
        if (this.fiChecklist2ReportHelperService.isFilterEmpty(this.currentFilter)) return false;

        if (this.fiChecklist2ReportHelperService.isCustomReportWidget(this.reportParam))
            return this.isCustomWidgetFilterChanged();

        return !isEqual(this.initialFilter, this.currentFilter);
    }

    private isCustomWidgetFilterChanged(): boolean {
        const initialFilterCopy: IChecklistV2ReportParameterFilter = cloneDeep(this.initialFilter);
        delete initialFilterCopy.Name;
        delete initialFilterCopy.Guid;
        const currentFilterCopy: IChecklistV2ReportParameterFilter = cloneDeep(this.currentFilter);
        delete currentFilterCopy.Name;
        delete currentFilterCopy.Guid;
        return !isEqual(currentFilterCopy, initialFilterCopy);
    }

    onCurrentFilterChanged() {
        const isFilterChanged: boolean = this.isFilterChanged();
        this._hasFilterUnsavedChanges = isFilterChanged;
        this.currentFilterChange.emit(this.currentFilter);
        this.fiChecklist2ReportHelperService.changeSaveFilterButtonVisibilityChange(isFilterChanged);
    }

    updateCurrentFilter() {
        this.initialFilter = cloneDeep(this.currentFilter);
        const filterIndex: number = this.reportParam.Settings.Filters.findIndex(
            (filter: IChecklistV2ReportParameterFilter) => filter.Guid === this.currentFilter.Guid,
        );
        this.reportParam.Settings.Filters[filterIndex] = this.currentFilter;
        this.saveReportParameter().then(() => {
            this.itemsListFilterDialogSnackbarService.showFilterUpdatedSnackbar(this.getUpdatedFilterName());
            this.onCurrentFilterChanged();
            this.reportParamChange.emit(this.reportParam);
            this.fiChecklist2ReportHelperService.triggerReportParamUpdated(this.reportParam);
        });
    }

    private getUpdatedFilterName(): string {
        return this.fiChecklist2ReportHelperService.isCustomReportWidget(this.reportParam)
            ? this.reportParam.Name
            : this.currentFilter.Name;
    }

    resetCurrentFilter() {
        this.currentFilter = cloneDeep(this.initialFilter);
        this.onCurrentFilterChanged();
        this.reportParamChange.emit(this.reportParam);
    }

    protected getNewFilterInstance(
        existingFilter: IChecklistV2ReportParameterFilter,
    ): IChecklistV2ReportParameterFilter {
        // we should return deep copy of existing filter with new guid and empty name
        return Object.assign({}, existingFilter, { Guid: guid(), Name: '' });
    }

    toggleFilterContainerVisibility() {
        this.isFilterExpanded$.next(!this.isFilterExpanded$.value);
    }

    saveFilter(args: ISaveChecklist2ReportFilterArgs) {
        if (!args.updateCurrentFilter) {
            this.saveFilterInternal();
        } else {
            this.fiDialogsService
                .confirm({
                    yesButtonText: this.appTextService.get('common.proceed_and_save'),
                    headerText: this.appTextService.get('widget.update_changes_header'),
                    messageText: this.appTextService.get('widget.update_changes_message'),
                    isTopCloseButtonShown: true,
                    headerIconClass: 'far fa-bookmark BrandedColor',
                    preventUppercaseButtonLabel: true,
                    config: {
                        panelClass: 'fi-rounded-dialog-panel',
                    },
                })
                .onYesButtonClick(() => {
                    this.updateCurrentFilter();
                });
        }
    }

    protected doPreventNewFilterInstanceAfterSave = false;
    private saveFilterInternal() {
        const newFilter: IChecklistV2ReportParameterFilter = this.getNewFilterInstance(this.currentFilter);

        this.itemsListFilterDialogSnackbarService
            .getSaveFilterDialog(newFilter.Name)
            .onYesButtonClick((filterName: string) => {
                newFilter.Name = filterName;
                this.reportParam.Settings.Filters.push(newFilter);
                //this.reportParam.Settings.CurrentFilterGuid = newFilter.Guid;

                if (this.doPreventNewFilterInstanceAfterSave) {
                    this.currentFilter = newFilter;
                    this.reportParam.Settings.CurrentFilterGuid = this.currentFilter.Guid;
                } else {
                    this.currentFilter = this.getNewFilterInstance(newFilter);
                }

                this.saveReportParameter().then(() => {
                    this.itemsListFilterDialogSnackbarService.showFilterSavedSnackbar(newFilter.Name);
                    this.reportParamChange.emit(this.reportParam);
                    this.fiChecklist2ReportHelperService.triggerSaveFilterFinished();
                    this.fiChecklist2ReportHelperService.triggerReportParamUpdated(this.reportParam);
                });
            })
            .afterClosed(() => (this.saveChangesWarningService.closeDialogOnTransition = false));
        this.saveChangesWarningService.closeDialogOnTransition = true;
    }

    saveFilterAsNewWidget() {
        this.itemsListFilterDialogSnackbarService
            .getSaveFilterAsWidgetDialog(
                this.appTextService.get('checklist.filter_save_new_widget'),
                this.appTextService.get('checklist.filter_save_new_widget_description'),
            )
            .onYesButtonClick((widgetName: string) => {
                const newReportParameter: IChecklistV2ReportParameter = {
                    Name: widgetName,
                    Description: '',
                    ReportType: this.reportParam.ReportType,
                    SortIndex: this.reportParametersCount,
                    Settings: {
                        CurrentFilterGuid: this.currentFilter.Guid,
                        Filters: [this.currentFilter],
                    },
                };
                this.checklist2ReportService
                    .saveReportParameter(newReportParameter, this.reportData.auditMode)
                    .then((reportParameterId: number) => {
                        this.checklist2ReportService
                            .getDataForReportParameter(newReportParameter, this.reportData.auditMode)
                            .then((data) => {
                                this.reportData.setReportData(data, reportParameterId);

                                this.itemsListFilterDialogSnackbarService.showWidgetAddedSnackbar();
                                newReportParameter.Id = reportParameterId;

                                this.fiChecklist2ReportHelperService.addNewParameter(newReportParameter);
                                this.currentFilter = this.getNewFilterInstance(this.currentFilter);
                                this.reportParamChange.emit(this.reportParam);
                            });
                    });
            })
            .afterClosed(() => (this.saveChangesWarningService.closeDialogOnTransition = false));
        this.saveChangesWarningService.closeDialogOnTransition = true;
    }

    onSavedFiltersChange() {
        this.saveReportParameter().then(() => {
            this.fiChecklist2ReportHelperService.triggerReportParamUpdated(this.reportParam);
        });
    }

    protected saveReportParameter(): Promise<number> {
        return this.checklist2ReportService.saveReportParameter(this.reportParam, this.reportData.auditMode);
    }

    protected ngOnDestroy$: Subject<void> = new Subject<void>();
    ngOnDestroy() {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }
}
